import { useLayoutEffect } from 'react';
import { useLocation } from 'react-router-dom';

export default function RouteLogger() {
  const location = useLocation();

  const scrollToHashTarget = (hash, behavior = 'smooth') => {
    if (!hash) {
      return false;
    }

    const target = document.getElementById(hash);

    if (!target) {
      return false;
    }

    target.scrollIntoView({ behavior, block: 'start' });
    return true;
  };

  useLayoutEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const handleHashChange = () => {
      const hash = window.location.hash ? decodeURIComponent(window.location.hash.slice(1)) : '';

      if (!hash) {
        return;
      }

      scrollToHashTarget(hash);
    };

    window.addEventListener('hashchange', handleHashChange);
    handleHashChange();

    return () => window.removeEventListener('hashchange', handleHashChange);
  }, [location.pathname, location.search]);

  useLayoutEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }

    const hash = location.hash ? decodeURIComponent(location.hash.slice(1)) : '';

    if (hash) {
      if (scrollToHashTarget(hash, 'auto')) {
        return undefined;
      }

      let attempts = 0;
      const maxAttempts = 40;
      const intervalId = window.setInterval(() => {
        attempts += 1;

        if (scrollToHashTarget(hash, 'auto') || attempts >= maxAttempts) {
          window.clearInterval(intervalId);
        }
      }, 100);

      return () => window.clearInterval(intervalId);
    }

    // Only a new page starts at the top. A changed query on the same page
    // (filters, sort, search) keeps the shopper where they are.
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    document.documentElement?.scrollTo?.({ top: 0, left: 0, behavior: 'auto' });
    document.body?.scrollTo?.({ top: 0, left: 0, behavior: 'auto' });
    return undefined;
  }, [location.pathname, location.hash]);

  return null;
}
